import type { ReactElement } from "react";
import type { LamaReportOverviewOutput } from "@playbookai/widget-schemas";
import { KPIValue } from "../../atoms/KPIValue/index.js";
import { StatusPill } from "../../atoms/StatusPill/StatusPill.js";
import type { WidgetBodyProps } from "../types.js";
import {
  LAMA_CONFIDENCE_TONE,
  LAMA_LIVE_WAVE_NOTE,
  LAMA_MODE_LABEL,
  LAMA_SUPPRESSED_BODY,
  LAMA_SUPPRESSED_TITLE,
  lamaConfidenceLabel,
  lamaRespondedNote,
} from "../lama-report-presentation.js";

/**
 * LAMA Report Overview — the campaign's attribute badges plus the latest
 * wave's PARTICIPATION and CONFIDENCE, ported data-blind from `LamaReportPage`.
 * Governed actions (See responses / Export / Share) stay HOST chrome and are
 * deliberately not part of this widget.
 *
 * NO MATURITY HEADLINE HERE (review note 10, 2026-09-10 ADR-0027 amendment).
 * This band used to lead with "Overall maturity" and two deltas computed by the
 * pre-v3 equal-category mean, beside a Maturity Overview section computing the
 * equal-pillar figure — two headlines that disagree on any version whose
 * pillars hold different numbers of categories. The report has ONE headline,
 * on the hierarchy sections, drawn per Baseline / Responses / Compare view; the
 * payload's `overall`/`delta`/`sinceBaseline` are v3 now but are not shown
 * here, so nothing on this band can compete with it.
 *
 * Two states the numbers alone cannot express get their own copy: a `live`
 * latest wave (still open, so the figures move) and `suppressed` scores. The
 * second is now reachable only from a FROZEN wave recorded under the
 * minimum-respondents rule that was removed on 2026-09-15 (Task 3 of
 * `docs/planning/lama-experience-simplification-plan.md`), and its copy says
 * so — no live figure is withheld for a respondent count any more.
 *
 * **PARTICIPATION IS TWO FIGURES, and the second is the one that replaced the
 * threshold.** `participation` is a PERCENTAGE over assignment rows — a
 * completion rate over work issued — and `respondedPeople`/`totalPeople` are
 * DISTINCT PEOPLE. They answer different questions, so both render; the people
 * pair is the headline the plan asks for ("X / total people have responded")
 * and the percentage is its detail line.
 *
 * The pair is OPTIONAL on the dataset, so an older resolver renders the
 * percentage alone rather than a fabricated `0 of 0`. A campaign with NO wave
 * yet (`waveCount: 0` — a finalised baseline is its whole report) says "No
 * responses yet" rather than a zero.
 */
export function LamaReportOverviewWidget({
  data,
}: WidgetBodyProps<LamaReportOverviewOutput>): ReactElement {
  return (
    <div>
      <div className="flex flex-wrap gap-1.5">
        <StatusPill tone="neutral">{LAMA_MODE_LABEL[data.mode]}</StatusPill>
        <StatusPill tone="neutral">{data.cadence}</StatusPill>
        <StatusPill tone="neutral">{data.scopeLabel}</StatusPill>
        <StatusPill tone="neutral">
          {data.waveCount} wave{data.waveCount === 1 ? "" : "s"}
        </StatusPill>
        {data.latestWaveLive ? (
          <StatusPill tone="watch">Live · wave open</StatusPill>
        ) : null}
      </div>
      {data.latestWaveLive ? (
        <p className="mt-2 text-xs text-pb-text-muted">{LAMA_LIVE_WAVE_NOTE}</p>
      ) : null}
      {data.suppressed ? (
        <div className="mt-3 rounded-md border border-pb-border p-3">
          <p className="text-sm font-medium text-pb-text">
            {LAMA_SUPPRESSED_TITLE}
          </p>
          <p className="mt-1 text-sm text-pb-text-muted">
            {LAMA_SUPPRESSED_BODY}
          </p>
        </div>
      ) : null}
      {data.waveCount === 0 ? (
        <p className="mt-3 text-sm text-pb-text-muted">
          No responses yet — participation and confidence appear once a wave has
          run.
        </p>
      ) : (
        <div className="mt-5 grid grid-cols-2 gap-4 @lg/content:grid-cols-4">
          {/* The 30px hero figure is KPIValue lg (look row SN-16). */}
          {data.respondedPeople === undefined ||
          data.totalPeople === undefined ? (
            /* An older resolver carries no people pair. The percentage is
               still a true statement about work issued, so it leads instead —
               never a fabricated "0 of 0". */
            <KPIValue
              variant="label"
              size="lg"
              label={`Responded · ${data.latestWaveLabel}`}
              value={`${data.participation}%`}
              description="of assignments completed"
            />
          ) : (
            /* The sentence in full, so the pair is never read as a ratio of
               something else; the percentage rides with it as the completion
               rate over assignments, which is a different question and must
               not be confused for this one. */
            <KPIValue
              variant="label"
              size="lg"
              label={`Responded · ${data.latestWaveLabel}`}
              value={
                <>
                  {data.respondedPeople}
                  <span className="text-pb-text-muted">
                    {" / "}
                    {data.totalPeople}
                  </span>
                </>
              }
              description={
                <>
                  {lamaRespondedNote(data.respondedPeople, data.totalPeople)} ·{" "}
                  {data.participation}% of assignments completed
                </>
              }
            />
          )}
          <div>
            <p className="text-xs text-pb-text-muted">Confidence</p>
            <div className="mt-1.5">
              <StatusPill tone={LAMA_CONFIDENCE_TONE[data.confidence]}>
                {lamaConfidenceLabel(data.confidence)}
              </StatusPill>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
